// HSE calculation helpers — used across dashboard, registers and reports.
// All functions are pure and defensive so pages never crash on missing data.

export const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

function n(v) {
  const x = Number(v);
  return isFinite(x) ? x : 0;
}

function sumArr(a) {
  return Array.isArray(a) ? a.reduce((s, v) => s + n(v), 0) : 0;
}

// TRIR = (Recordable Incidents * 200,000) / Manhours
// Recordable = MTC + RWC + LTI + Fatality + Occupational Illness (FAC IS EXCLUDED!)
export function calculateTRIR({ fac = 0, mtc = 0, rwc = 0, lti = 0, fatality = 0, occupationalIllness = 0, manhours = 0 }) {
  const mh = n(manhours);
  if (mh <= 0) return 0;
  const rec = n(mtc) + n(rwc) + n(lti) + n(fatality) + n(occupationalIllness);
  return +((rec * 200000) / mh).toFixed(2);
}

// LTIFR = LTI * 1,000,000 / Manhours
export function calculateLTIFR({ lti = 0, manhours = 0 }) {
  const mh = n(manhours);
  if (mh <= 0) return 0;
  return +((n(lti) * 1000000) / mh).toFixed(2);
}

// Severity Rate = Lost Days * 200,000 / Manhours
export function calculateSeverityRate({ lostDays = 0, manhours = 0 }) {
  const mh = n(manhours);
  if (mh <= 0) return 0;
  return +((n(lostDays) * 200000) / mh).toFixed(2);
}

// Closure % = closed / total * 100
export function calculateClosurePercent(closed, total) {
  const t = n(total);
  if (t <= 0) return 0;
  return +((n(closed) / t) * 100).toFixed(1);
}

// Aggregate a monthly-KPI store for a given site+metric across the year.
export function sumMonthlyKPI(monthlyKPI, site, metric) {
  if (!monthlyKPI || !site) return 0;
  const series = monthlyKPI[site]?.[metric];
  return sumArr(series);
}

// Aggregate metric across all sites for full year.
export function sumMetricAllSites(monthlyKPI, metric) {
  if (!monthlyKPI) return 0;
  return Object.keys(monthlyKPI).reduce((acc, site) => acc + sumArr(monthlyKPI[site]?.[metric]), 0);
}

// Days between today and target date.  Positive = overdue, negative = still time.
export function calculateDaysOverdue(targetDate, status) {
  if (!targetDate) return 0;
  if (status === "Closed" || status === "Approved") return 0;
  const target = new Date(targetDate);
  if (isNaN(target.getTime())) return 0;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  target.setHours(0, 0, 0, 0);
  const diff = Math.floor((today.getTime() - target.getTime()) / 86400000);
  return diff > 0 ? diff : 0;
}

// Returns "Valid" | "Expiring Soon" | "Expired" — 30-day warning window.
export function getExpiryStatus(expiryDate, warningDays = 30) {
  if (!expiryDate) return "Valid";
  const exp = new Date(expiryDate);
  if (isNaN(exp.getTime())) return "Valid";
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  exp.setHours(0, 0, 0, 0);
  const diff = Math.floor((exp.getTime() - today.getTime()) / 86400000);
  if (diff < 0) return "Expired";
  if (diff <= warningDays) return "Expiring Soon";
  return "Valid";
}

// Days until expiry (negative if expired).
export function daysUntilExpiry(expiryDate) {
  if (!expiryDate) return null;
  const exp = new Date(expiryDate);
  if (isNaN(exp.getTime())) return null;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  exp.setHours(0, 0, 0, 0);
  return Math.floor((exp.getTime() - today.getTime()) / 86400000);
}

// Consistent formatter for KPI card counts.
export function fmtNum(v) {
  const num = Number(v);
  if (v === null || v === undefined || v === "" || isNaN(num)) return "0";
  if (Math.abs(num) >= 1000) return num.toLocaleString();
  return String(num);
}

export function fmtRate(v) {
  const num = Number(v);
  if (v === null || v === undefined || v === "" || isNaN(num)) return "0.00";
  return num.toFixed(2);
}
